"use client";

import { AppSidebar, type DbTeamRef } from "@/components/app-sidebar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { SIDEBAR_ROUTES } from "@/lib/sidebar-routes";
import { supabase } from "@/lib/supabase";
import type { DbModelTier } from "@/types/db";
import { MenuIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

const CodeSpacesAccessContext = createContext(false);

export function useCodeSpacesAccess() {
  return useContext(CodeSpacesAccessContext);
}

type SubPageUser = {
  name: string;
  initials: string;
};

type SubPageLayoutProps = {
  children: ReactNode;
  requireWorkflowsAccess?: boolean;
  workflowsAllowedModelTiers?: DbModelTier[] | null;
  workflowsEnabled?: boolean;
  requireCodeSpacesAccess?: boolean;
  codeSpacesAllowedModelTiers?: DbModelTier[] | null;
};

export function SubPageLayout({
  children,
  requireWorkflowsAccess = false,
  workflowsAllowedModelTiers,
  workflowsEnabled: workflowsEnabledOverride,
  requireCodeSpacesAccess = false,
  codeSpacesAllowedModelTiers,
}: SubPageLayoutProps) {
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userModelTier, setUserModelTier] = useState<DbModelTier>("standard");
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [teams, setTeams] = useState<DbTeamRef[]>([]);
  const [activeTeamId, setActiveTeamId] = useState<string | null>(null);
  const [sidebarUser, setSidebarUser] = useState<SubPageUser>({ name: "Profile", initials: "U" });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) {
        if (!cancelled) setIsProfileLoaded(true);
        return;
      }

      setSessionUserId(user.id);

      const [profileResult, teamsResult] = await Promise.all([
        supabase.from("profiles").select("role, model_tier, display_name, username").eq("user_id", user.id).single(),
        supabase.from("teams").select("id, user_id"),
      ]);

      if (cancelled) return;

      if (profileResult.data) {
        const { role, model_tier, display_name, username } = profileResult.data;
        setUserRole(role ?? null);
        setUserModelTier(model_tier ?? "standard");
        const raw = display_name?.trim() || username?.trim() || user.email?.split("@")[0] || "User";
        const initials = raw.trim().split(/\s+/).filter(Boolean).map((p: string) => p[0]?.toUpperCase() ?? "").join("").slice(0, 2) || "U";
        setSidebarUser({ name: raw, initials });
      }

      if (teamsResult.data) {
        const teamRefs = teamsResult.data as DbTeamRef[];
        setTeams(teamRefs);
        const personal = teamRefs.find((t) => t.user_id === user.id);
        setActiveTeamId(personal?.id ?? teamRefs[0]?.id ?? null);
      }

      setIsProfileLoaded(true);
    })();

    return () => { cancelled = true; };
  }, []);

  const workflowsEnabled = useMemo(() => {
    if (typeof workflowsEnabledOverride === "boolean") return workflowsEnabledOverride;
    return workflowsAllowedModelTiers !== undefined
      && workflowsAllowedModelTiers !== null
      && workflowsAllowedModelTiers.includes(userModelTier);
  }, [userModelTier, workflowsAllowedModelTiers, workflowsEnabledOverride]);

  useEffect(() => {
    if (requireWorkflowsAccess && isProfileLoaded && !workflowsEnabled) {
      router.replace("/");
    }
  }, [isProfileLoaded, requireWorkflowsAccess, router, workflowsEnabled]);

  const codeSpacesEnabled = useMemo(() => {
    return codeSpacesAllowedModelTiers !== undefined
      && codeSpacesAllowedModelTiers !== null
      && codeSpacesAllowedModelTiers.includes(userModelTier);
  }, [codeSpacesAllowedModelTiers, userModelTier]);

  useEffect(() => {
    if (requireCodeSpacesAccess && isProfileLoaded && !codeSpacesEnabled) {
      router.replace("/");
    }
  }, [isProfileLoaded, requireCodeSpacesAccess, router, codeSpacesEnabled]);

  const handleItemClick = (item: string) => {
    const route = SIDEBAR_ROUTES[item as keyof typeof SIDEBAR_ROUTES];
    router.push(route ?? `/?nav=${item}`);
  };

  const sharedSidebarProps = {
    onItemClick: handleItemClick,
    onProfileClick: () => router.push("/"),
    userRole,
    user: sidebarUser,
    sessionUserId,
    teams,
    activeTeamId,
    workflowsEnabled,
    codeSpacesEnabled,
  };

  if (requireWorkflowsAccess && isProfileLoaded && !workflowsEnabled) {
    return null;
  }

  if (requireCodeSpacesAccess && isProfileLoaded && !codeSpacesEnabled) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex overflow-hidden overscroll-none bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <AppSidebar {...sharedSidebarProps} />
      </div>

      {/* Mobile hamburger */}
      <div className="absolute left-0 top-0 z-50 p-2 md:hidden">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <Button
            aria-label="Open menu"
            className="h-10 w-10"
            onClick={() => setMobileMenuOpen(true)}
            size="icon"
            type="button"
            variant="ghost"
          >
            <MenuIcon size={24} />
          </Button>
          <SheetContent className="w-48 p-0" side="left">
            <VisuallyHidden>
              <SheetTitle>Navigation menu</SheetTitle>
              <SheetDescription>App navigation and account links</SheetDescription>
            </VisuallyHidden>
            <AppSidebar
              {...sharedSidebarProps}
              listMode
              onNotificationsNavigate={() => setMobileMenuOpen(false)}
              onItemClick={(item) => {
                setMobileMenuOpen(false);
                handleItemClick(item);
              }}
              onProfileClick={() => {
                setMobileMenuOpen(false);
                router.push("/");
              }}
            />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CodeSpacesAccessContext.Provider value={isProfileLoaded && codeSpacesEnabled}>
          {children}
        </CodeSpacesAccessContext.Provider>
      </div>
    </div>
  );
}
